import React, { memo, useEffect, useState } from 'react'
import {
  Button,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text
} from '@chakra-ui/react'

import { BaseFormField } from 'components/ChakraToolkit/FormField'

interface GuarantorBondIdModalProps {
  isOpen: boolean
  close: () => void
  currentBondId: string | null
  save: (bondId: string | null) => Promise<boolean>
  isSaving: boolean
}

const GuarantorBondIdModal: React.FC<GuarantorBondIdModalProps> = ({
  isOpen,
  close,
  currentBondId,
  save,
  isSaving
}) => {
  const [bondId, setBondId] = useState<string>(currentBondId ?? '')

  useEffect(() => {
    if (isOpen) {
      setBondId(currentBondId ?? '')
    }
  }, [isOpen, currentBondId])

  const trimmed = bondId.trim()
  const hasChanges = trimmed !== (currentBondId ?? '')
  const canSave = !!trimmed && hasChanges && !isSaving

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <ModalContent>
        <ModalHeader>
          <Heading size='h4'>Guarantor Bond ID</Heading>
        </ModalHeader>
        <ModalBody>
          <Stack direction='column' spacing={4}>
            <Text fontSize='sm'>
              {currentBondId
                ? 'This application is flagged as a guarantor lease. Removing the bond ID will revert it to generating a regular lease.'
                : 'Setting a bond ID will flag this application so that a guarantor lease is generated instead of a regular lease.'}
            </Text>
            <BaseFormField<{ bondId: string }>
              field='bondId'
              label='Bond ID'
              errors={{ bondId: '' }}
              isRequired
            >
              <Input
                value={bondId}
                onChange={(e) => setBondId(e.target.value)}
                placeholder='Enter bond ID'
                autoFocus
              />
            </BaseFormField>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack direction='row' spacing={2} width='100%' justifyContent='flex-end'>
            {currentBondId && (
              <Button
                colorScheme='red'
                variant='ghost'
                size='xs'
                onClick={async () => {
                  if (await save(null)) {
                    close()
                  }
                }}
                isDisabled={isSaving}
                isLoading={isSaving}
              >
                Remove
              </Button>
            )}
            <Button size='xs' variant='ghost' onClick={close} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              colorScheme='teal'
              size='xs'
              onClick={async () => {
                if (await save(trimmed)) {
                  close()
                }
              }}
              isDisabled={!canSave}
              isLoading={isSaving}
            >
              Save
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default memo(GuarantorBondIdModal)
